import { CheckCheck, Loader2, Sparkles, Trash2 } from "lucide-react";
import { OfferForm as CustomPlanForm } from "@/components/offers/OfferForm";
import { TrialTools } from "@/components/admin/TrialTools";
import { MinisiteScope } from "@/components/admin/MinisiteScope";
import { useEuro } from "@/components/market/Plans";
import { useI18n } from "@/i18n/I18nContext";

const flexPrice = (o, locale) => {
  const q = o.quote;
  const f = new Intl.NumberFormat(locale, { style: "currency", currency: (o.terms?.currency || "eur").toUpperCase() });
  return o.terms?.mode === "prepaid" ? `${f.format(q.total_ht)} · 1×` : `${f.format(q.period_ht_cur)} / ${o.terms?.interval || 1}`;
};

export const activeOffer = (d) => (d?.custom_offer && !d.custom_offer.archived ? d.custom_offer : null);

export const PlanBadge = ({ testId }) => {
  const { t } = useI18n();
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-fuchsia-400/60 bg-fuchsia-500/15 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wide text-fuchsia-200" data-testid={testId}>
      <Sparkles className="h-3 w-3" /> {t("msg.badgePlan")}
    </span>
  );
};

export const PlanStatus = ({ d, testId }) => {
  const { t, locale } = useI18n();
  const euro = useEuro();
  const offer = activeOffer(d);
  if (!offer) return null;
  const paid = d.plan === "custom" && ["active", "trialing"].includes(d.stripe_status);
  return (
    <span className={`inline-flex max-w-full flex-wrap items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${paid ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-200"}`} data-testid={testId}>
      <bdi>{t(offer.apps ? "custom.statusOffered" : "custom.statusOfferedMini", { n: new Intl.NumberFormat().format(offer.apps), price: offer.flex && offer.quote ? flexPrice(offer, locale) : euro(offer.eur) })}</bdi>
      {paid && <> · {t("custom.statusPaid")}</>}
    </span>
  );
};

export const PlanButton = ({ d, onClick, testId }) => {
  const { t } = useI18n();
  return (
    <button type="button" className="btn-gold btn-sm" onClick={onClick} data-testid={testId}>
      <Sparkles className="h-4 w-4" /> {t(activeOffer(d) ? "custom.editBtn" : "custom.createBtn")}
    </button>
  );
};

const ThreadTools = ({ conv, onTreated, onDelete }) => {
  const { t } = useI18n();
  return (
    <>
      <button type="button" onClick={onTreated} aria-pressed={conv.treated} data-testid="conv-treated-btn"
        className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors duration-150 ${conv.treated ? "border-emerald-400 bg-emerald-500/15 text-emerald-200" : "border-neutral-600 text-neutral-200 hover:border-emerald-400"}`}>
        <CheckCheck className="h-4 w-4 shrink-0" /> {t(conv.treated ? "msg.treatedOn" : "msg.treated")}
      </button>
      <button type="button" onClick={onDelete} className="btn-red btn-sm" data-testid="conv-delete-btn">
        <Trash2 className="h-4 w-4" /> {t("msg.deleteThread")}
      </button>
    </>
  );
};

export const ConvPlanBar = ({ conv, d, open, setOpen, onChanged, onTreated, onDelete }) => {
  const mini = conv.client_kind === "minisite";
  const scope = mini ? conv.minisite_scope : null;
  const canOffer = !mini || !!scope;  // mini-site plans: only once app + REF are known (header or picked by hand)
  return (
  <div id="conv-plan-bar" className="border-b border-neutral-800 p-3" data-testid="conv-plan-bar">
    {mini && <MinisiteScope conv={conv} onChanged={onChanged} />}
    <div className="flex flex-wrap items-center gap-2">
      {conv.category === "custom" && <PlanBadge testId="conv-plan-badge" />}
      {canOffer && <PlanButton d={d} onClick={() => setOpen((o) => !o)} testId="conv-custom-plan-btn" />}
      <ThreadTools conv={conv} onTreated={onTreated} onDelete={onDelete} />
      <PlanStatus d={d} testId="conv-plan-status" />
      <TrialTools d={d} onChanged={onChanged} />
    </div>
    {open && canOffer && (d ? (
      <CustomPlanForm key={`${conv.id}-${d.custom_offer?.id || "none"}-${scope?.key_id || ""}`} d={d} initial={conv.plan_request} conversationId={conv.id} scope={scope} onChanged={onChanged} />
    ) : <Loader2 className="mt-3 h-5 w-5 animate-spin text-[var(--gold)]" data-testid="conv-plan-loading" />)}
  </div>
  );
};
